import { Button } from '@beek/design-system';
import { Divider, Typography } from 'beek-design-system';
import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import Audiocard, {
  IAudiobook,
} from 'src/components/Article/Audiocard/Audiocard';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import styled from 'styled-components';
import useCheckSession from 'src/utils/useCheckSession';

type AudioContent = Omit<IAudiobook, 'content'>;

export interface IAudioContent extends AudioContent {
  publisher: string;
  sample: string;
}

type ContentProps = {
  book: IAudioContent;
};

const ContentDetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: 2.5rem;
  margin-bottom: 5rem;
  @media ${({ theme }): string => theme.devices.laptop} {
    margin: 0 auto;
    margin-top: 4.3125rem;
    margin-bottom: 12.5rem;
    flex-direction: row;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  justify-content: flex-start;
  @media ${({ theme }): string => theme.devices.laptop} {
    margin-left: 2.9375rem;
    margin-top: 3.75rem;
  }
`;

const ContentCTA = styled.div`
  display: none;
  @media ${({ theme }): string => theme.devices.laptop} {
    display: flex;
    font-weight: bold;
    flex-direction: column;
    width: 100%;
  }
`;

const NormalCTA = styled.div`
  margin-top: 1.25rem;
  @media ${({ theme }): string => theme.devices.laptop} {
    margin-top: 1.5rem;
    width: 100%;
    border-radius: 0;
    &:active {
      color: var(--gray-100);
      background: var(--snow-blue);
    }
  }
`;

const VerticalDivider = styled(Divider)`
  height: 28.125rem;
  width: 0;
  align-self: flex-start;
  display: none;
  @media ${({ theme }): string => theme.devices.laptop} {
    display: flex;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-self: flex-start;
`;

const InfoWrapper = styled.div`
  display: none;
  @media ${({ theme }): string => theme.devices.laptop} {
    display: block;
    margin-left: 1.25rem;
  }
`;

const PublisherInfo = (
  language: string,
  duration: string,
  publisher: string
): JSX.Element => {
  const t = useTranslations('articles');
  return (
    <InfoWrapper>
      <Typography type="body2" colorVariant="gray70">{`${t(
        'audioCard.language'
      )} ${language}`}</Typography>
      <Typography type="body2" colorVariant="gray70">{`${t(
        'audioCard.duration'
      )} ${duration}`}</Typography>
      <Typography type="body2" colorVariant="gray70">{`${t(
        'audioCard.publisher'
      )} ${publisher}`}</Typography>
    </InfoWrapper>
  );
};

const DynamicButton = dynamic(() => import('./DynamicButton'), { ssr: false });

const AudioContentCard = ({ book }: ContentProps): JSX.Element => {
  const { language, duration, publisher, sample } = book;
  const t = useTranslations('audioContent');
  const [activePlayer, setActivePlayer] = useState(false);
  const session = useCheckSession();

  useEffect(() => {
    const interval = setInterval(() => {
      setActivePlayer(true);
    }, 5000);
    return (): void => clearInterval(interval);
  }, []);

  return (
    <ContentDetailContainer>
      <Wrapper>
        <Audiocard audiobook={book} type="contentDetail" />
        {PublisherInfo(language, duration, publisher)}
      </Wrapper>
      <VerticalDivider colorVariant="snowBlue" />
      <ButtonsContainer>
        <ContentCTA>
          <Link href={session ? '/subscription/start' : '/signup'} passHref>
            <Button block>{`${t('primaryCta')}`}</Button>
          </Link>
        </ContentCTA>
        {activePlayer ? (
          <DynamicButton data-cy="playSampleButton" sample={sample} />
        ) : (
          <NormalCTA data-cy="playSampleButton">
            <Button type="text">{`${t('loading')}`}</Button>
          </NormalCTA>
        )}
      </ButtonsContainer>
    </ContentDetailContainer>
  );
};

export default AudioContentCard;
